/** /users/sessions — all CPTokens, filterable + searchable by fingerprint. */
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
} from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

import { ConsoleApiError } from '@/api/client';
import * as sessionsApi from '@/api/sessions';
import { AUTH_TYPES, type AccessScope, type SessionListParams } from '@/api/sessions';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { PageHeader } from '@/components/PageHeader';
import {
  TenantPicker,
  pickedSentinel,
  type TenantPickerValue,
} from '@/components/TenantPicker/TenantPicker';
import { boundToParam, paramToBound, TimeWindowPicker, type TimeBound } from '@/components/TimeWindowPicker';
import { UserPicker } from '@/components/UserPicker/UserPicker';
import { useSnackbar } from '@/components/Snackbar';
import { useGridFilters, type FilterDef } from '@/hooks/useGridFilters';
import { makeResourceFiltersAdapter, useGridPresets } from '@/hooks/useGridPresets';
import { usePermissions } from '@/hooks/usePermissions';
import { formatTenantIds } from '@/scope/tenantIds';
import { usePageTenant } from '@/scope/usePageTenant';
import { PAGE_FILL_SX } from '@/layout/pageFill';

import { fingerprintCandidates } from './fingerprint';
import { SessionsGrid } from './SessionsGrid';

const SCOPES: AccessScope[] = ['dashboard', 'console', 'user', 'booking'];

/** Sentinel picker option: system tokens (`tour_operator IS NULL`). Exclusive with
 *  the tenants (D3): picked, it is the picker's whole value. */
const SYSTEM_TENANT: TenantPickerValue = { id: 0, name: 'System only (no tenant)' };
const SYSTEM_EXTRA_OPTIONS = [SYSTEM_TENANT];

/** Header-row filters for the Scope / Auth columns — share the same `access`
 *  / `auth` URL params the toolbar writes (project grid rule). */
const SESSION_HEADER_FILTERS: FilterDef[] = [
  { param: 'access', column: { field: 'access', operator: 'is' } },
  { param: 'auth', column: { field: 'auth_display', operator: 'is' } },
  { param: 'booking', column: { field: 'booking', operator: 'equals' } },
];

/** Presets cover every URL filter param (the local fingerprint search is
 *  component state and deliberately excluded). */
const PRESET_FILTERS = makeResourceFiltersAdapter(SESSION_HEADER_FILTERS, [
  'user',
  'tenant',
  'active',
  'created_from',
  'created_to',
  'expires_from',
  'expires_to',
]);

/** SessionsGrid column field order with showUser=true (keep in sync). */
const FIELD_ORDER = [
  'user',
  'tenant',
  'access',
  'auth_display',
  'token',
  'created',
  'expires',
  'booking',
];

export function SessionsPage() {
  const { hasPerm } = usePermissions();
  const qc = useQueryClient();
  const snackbar = useSnackbar();
  const navigate = useNavigate();
  const [, setSearchParams] = useSearchParams();
  const { sessionId } = useParams<{ sessionId?: string }>();
  const routeSessionId = sessionId && /^\d+$/.test(sessionId) ? Number(sessionId) : null;

  const [selection, setSelection] = useState<number[]>([]);
  const [confirmRevoke, setConfirmRevoke] = useState(false);
  const [extendOpen, setExtendOpen] = useState(false);
  const [extendSeconds, setExtendSeconds] = useState(24 * 3600);

  const [fingerprints, setFingerprints] = useState<string[] | null>(null);
  const [tokenSearchError, setTokenSearchError] = useState<string | null>(null);

  const presets = useGridPresets({
    grid: 'sessions',
    defaultOrder: FIELD_ORDER,
    defaultOrdering: '-created',
    filters: PRESET_FILTERS,
  });

  // Scope/Auth column filters (edited via the filter dialog), sharing the same
  // access/auth URL params the toolbar writes — the hook watches searchParams,
  // so toolbar edits reflect in the dialog and vice versa. A loaded preset
  // supplies filter values through the preset layer.
  const columnFilters = useGridFilters(SESSION_HEADER_FILTERS, presets.filtersLayer);
  // Layered read: the URL wins; a loaded preset fills the gaps underneath.
  const { getParam, beforeUrlWrite } = presets;

  const setParam = useCallback(
    (key: string, value: string | null) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          beforeUrlWrite(next);
          if (value === null || value === '') next.delete(key);
          else next.set(key, value);
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams, beforeUrlWrite],
  );

  const intParam = (key: string): number | null => {
    const v = getParam(key);
    return v && /^\d+$/.test(v) ? Number(v) : null;
  };

  const FILTER_PARAMS = [
    'access',
    'auth',
    'user',
    'tenant',
    'active',
    'booking',
    'created_from',
    'created_to',
    'expires_from',
    'expires_to',
  ];
  const anyFilterActive = FILTER_PARAMS.some((p) => getParam(p)) || fingerprints !== null;
  const resetAllFilters = useCallback(() => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        beforeUrlWrite(next);
        for (const p of FILTER_PARAMS) next.delete(p);
        return next;
      },
      { replace: true },
    );
    setFingerprints(null);
    setTokenSearchError(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setSearchParams, beforeUrlWrite]);

  const accessParam = getParam('access');
  const access = accessParam ? (accessParam.split(',') as AccessScope[]) : [];
  const authRaw = getParam('auth');
  const auth = authRaw && /^\d+$/.test(authRaw) ? Number(authRaw) : null;
  const userId = intParam('user');
  const bookingId = intParam('booking');
  // Ids (`12,845`, one or several operators) or the 'null' sentinel (system
  // tokens). Presets DO cover this param here, so the preset layer is read
  // through `presets.getParam`; the console scope (the whole list) sits
  // underneath both.
  const pageTenant = usePageTenant({
    multiple: true,
    getPresetParam: getParam,
    beforeWrite: beforeUrlWrite,
  });
  const tenantParam = pageTenant.raw;
  const isSystem = tenantParam === 'null';
  /** What the API's `tour_operator` param carries: `'12,845'` or `null`. */
  const tenantApiValue = isSystem ? 'null' : (formatTenantIds(pageTenant.ids) ?? undefined);
  const activeRaw = getParam('active');
  const active: 'all' | 'true' | 'false' =
    activeRaw === 'true' || activeRaw === 'false' ? activeRaw : 'all';

  // ISO instants or relative tokens (`now-1h`): the backend resolves a token per request.
  const dateParam = (key: string): TimeBound | null => paramToBound(getParam(key), { end: key.endsWith('_to') });
  const createdFrom = dateParam('created_from');
  const createdTo = dateParam('created_to');
  const expiresFrom = dateParam('expires_from');
  const expiresTo = dateParam('expires_to');
  const setDateRange = useCallback(
    (fromKey: string, toKey: string, from: TimeBound | null, to: TimeBound | null) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          beforeUrlWrite(next);
          const fromParam = boundToParam(from);
          const toParam = boundToParam(to);
          if (fromParam) next.set(fromKey, fromParam);
          else next.delete(fromKey);
          if (toParam) next.set(toKey, toParam);
          else next.delete(toKey);
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams, beforeUrlWrite],
  );

  const fixedFilters = useMemo<SessionListParams>(() => {
    const f: SessionListParams = {};
    if (access.length) f.access = access;
    if (auth !== null) f.auth = auth;
    if (userId !== null) f.user = userId;
    if (bookingId !== null) f.booking = bookingId;
    if (tenantApiValue) f.tour_operator = tenantApiValue;
    if (active !== 'all') f.active = active === 'true';
    if (fingerprints?.length) f.fingerprint = fingerprints;
    const g = (k: string) => getParam(k) || undefined;
    f.created_from = g('created_from');
    f.created_to = g('created_to');
    f.expires_from = g('expires_from');
    f.expires_to = g('expires_to');
    return f;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessParam, auth, userId, bookingId, tenantApiValue, active, fingerprints, getParam]);

  async function runTokenSearch(raw: string) {
    setTokenSearchError(null);
    const trimmed = raw.trim();
    if (!trimmed) {
      setFingerprints(null);
      return;
    }
    try {
      const candidates = await fingerprintCandidates(trimmed);
      if (!candidates.length) {
        setTokenSearchError('Enter a full token or 40-character fingerprint.');
        return;
      }
      setFingerprints(candidates);
    } catch {
      setTokenSearchError('Could not compute fingerprint.');
    }
  }

  const batchMutation = useMutation({
    mutationFn: ({ action, seconds }: { action: 'revoke' | 'extend'; seconds?: number }) =>
      sessionsApi.batch(action, selection, seconds),
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ['sessions'] });
      snackbar.show(`Affected ${data.affected} session(s).`, 'success');
      setSelection([]);
    },
    onError: (e: unknown) =>
      snackbar.show(e instanceof ConsoleApiError ? e.message : 'Batch action failed.', 'error'),
  });

  const canManage = hasPerm('console_session_manage');

  return (
    <Box sx={PAGE_FILL_SX}>
      <PageHeader
        helpStory="sessions-administration"
        title="Sessions"
        subtitle="Active and expired access tokens across all users and tenants."
      />

      <Stack direction="row" spacing={2} sx={{ mb: 2, flexWrap: 'wrap' }} alignItems="center">
        <TextField
          select
          size="small"
          label="Scope"
          value={access}
          SelectProps={{ multiple: true }}
          onChange={(e) => {
            const v = e.target.value as unknown as string[];
            setParam('access', v.length ? v.join(',') : null);
          }}
          sx={{ minWidth: 180 }}
        >
          {SCOPES.map((sc) => (
            <MenuItem key={sc} value={sc}>
              {sc}
            </MenuItem>
          ))}
        </TextField>

        <TextField
          select
          size="small"
          label="Auth type"
          value={auth === null ? '' : String(auth)}
          onChange={(e) => setParam('auth', e.target.value === '' ? null : e.target.value)}
          sx={{ width: 150 }}
        >
          <MenuItem value="">All</MenuItem>
          {Object.entries(AUTH_TYPES).map(([k, label]) => (
            <MenuItem key={k} value={k}>
              {label}
            </MenuItem>
          ))}
        </TextField>

        <div style={{ minWidth: 260 }}>
          {/* The multiple picker names the operators itself (one batched lookup). */}
          <TenantPicker
            multiple
            value={isSystem ? SYSTEM_EXTRA_OPTIONS : pageTenant.values}
            onChange={(next) =>
              pageTenant.setRaw(
                pickedSentinel(next, SYSTEM_EXTRA_OPTIONS)
                  ? 'null'
                  : formatTenantIds(next.map((t) => t.id)),
              )
            }
            extraOptions={SYSTEM_EXTRA_OPTIONS}
          />
        </div>
        <div style={{ minWidth: 260 }}>
          {/* Tokens exist for every population — guests included. */}
          <UserPicker
            kinds={['system', 'staff', 'guest']}
            value={userId !== null ? { id: userId, email: `user #${userId}` } : null}
            onChange={(u) => setParam('user', u ? String(u.id) : null)}
            placeholder="Any user"
          />
        </div>

        <TextField
          select
          size="small"
          label="Active"
          value={active}
          onChange={(e) => setParam('active', e.target.value === 'all' ? null : e.target.value)}
          sx={{ width: 120 }}
        >
          <MenuItem value="all">All</MenuItem>
          <MenuItem value="true">Active</MenuItem>
          <MenuItem value="false">Expired</MenuItem>
        </TextField>

        <TimeWindowPicker
          label="Created"
          allowOpenEnded
          value={[createdFrom, createdTo]}
          onChange={([from, to]) => setDateRange('created_from', 'created_to', from, to)}
        />
        <TimeWindowPicker
          label="Expires"
          allowOpenEnded
          allowFuture
          value={[expiresFrom, expiresTo]}
          onChange={([from, to]) => setDateRange('expires_from', 'expires_to', from, to)}
        />
      </Stack>

      {tokenSearchError && (
        <Alert severity="warning" sx={{ mb: 2 }} onClose={() => setTokenSearchError(null)}>
          {tokenSearchError}
        </Alert>
      )}

      {canManage && selection.length > 0 && (
        <Stack
          direction="row"
          spacing={1}
          sx={{ mb: 2, p: 1.5, bgcolor: 'background.paper', borderRadius: 1 }}
          alignItems="center"
        >
          <Box sx={{ flexGrow: 1 }}>{selection.length} selected</Box>
          <Button onClick={() => setExtendOpen(true)}>Extend TTL</Button>
          <Button color="error" onClick={() => setConfirmRevoke(true)}>
            Revoke
          </Button>
        </Stack>
      )}

      {presets.blocking ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <SessionsGrid
          fixedFilters={fixedFilters}
          queryKeyExtra={['page']}
          onSelectionChange={canManage ? setSelection : undefined}
          selectedId={routeSessionId}
          onOpenDetails={(id) =>
            navigate(id === null ? '/users/sessions' : `/users/sessions/${id}`)
          }
          filterModel={columnFilters.filterModel}
          onFilterModelChange={columnFilters.onFilterModelChange}
          orderedFields={presets.orderedFields}
          sortModel={presets.sortModel}
          onSortModelChange={presets.onSortModelChange}
          columnVisibilityModel={presets.columnVisibilityModel}
          onColumnVisibilityModelChange={presets.onColumnVisibilityModelChange}
          onColumnOrderChange={presets.onColumnOrderChange}
          createdRange={{
            onRange: (from, to) => setDateRange('created_from', 'created_to', from, to),
            onReset: () => setDateRange('created_from', 'created_to', null, null),
          }}
          expiresRange={{
            onRange: (from, to) => setDateRange('expires_from', 'expires_to', from, to),
            onReset: () => setDateRange('expires_from', 'expires_to', null, null),
          }}
          headerToolbar={{
            onResetAllFilters: resetAllFilters,
            resetDisabled: !anyFilterActive,
            exportFileName: 'sessions',
            // Submit-style search: Enter hashes the input locally (SHA-1) and
            // filters by fingerprint candidates; clearing resets the filter.
            search: true,
            searchOnSubmit: true,
            searchPlaceholder: 'Token or fingerprint, then Enter',
            onSearchChange: (v) => void runTokenSearch(v),
            presets: presets.toolbar,
          }}
        />
      )}

      {confirmRevoke && (
        <ConfirmDialog
          open
          title={`Revoke ${selection.length} session(s)?`}
          message="Selected tokens will be deleted and their holders signed out immediately."
          confirmLabel="Revoke"
          destructive
          onConfirm={() => {
            setConfirmRevoke(false);
            batchMutation.mutate({ action: 'revoke' });
          }}
          onCancel={() => setConfirmRevoke(false)}
        />
      )}

      <Dialog open={extendOpen} onClose={() => setExtendOpen(false)}>
        <DialogTitle>Extend {selection.length} session(s)</DialogTitle>
        <DialogContent>
          <TextField
            select
            fullWidth
            label="Extend by"
            value={extendSeconds}
            onChange={(e) => setExtendSeconds(Number(e.target.value))}
            sx={{ mt: 1, minWidth: 240 }}
          >
            <MenuItem value={3600}>+1 hour</MenuItem>
            <MenuItem value={8 * 3600}>+8 hours</MenuItem>
            <MenuItem value={24 * 3600}>+1 day</MenuItem>
            <MenuItem value={7 * 24 * 3600}>+7 days</MenuItem>
            <MenuItem value={30 * 24 * 3600}>+30 days</MenuItem>
          </TextField>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setExtendOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={() => {
              setExtendOpen(false);
              batchMutation.mutate({ action: 'extend', seconds: extendSeconds });
            }}
          >
            Extend
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
